<template>
  <div class="flex flex-col gap-y-1">
    <div :class="direction" class="w-full flex gap-x-4 gap-y-1">
      <label
        v-if="label"
        :for="fieldName"
        :class="labelClass"
        class="flex items-center gap-x-2 text-brand-black-secondary"
      >
        <div>
          <span> {{ t9nLabel }}</span>
          <span v-if="isRequired" class="text-brand-red ps-1">*</span>
        </div>
        <HintTippy v-if="hint" :hint="hint"></HintTippy>
      </label>
      <div class="w-full flex flex-col gap-y-1">
        <InputAppendage v-if="appendages?.top" v-bind="appendages">
          {{ $t(appendages?.top) }}
        </InputAppendage>
        <tippy
          :content="$t(`${popoverContent || ''}`)"
          :offset="[0, 8]"
          :theme="'dark-border'"
          :maxWidth="200"
          interactive
          trigger="mouseenter"
          placement="bottom"
        >
          <VDatePicker
            v-if="!readOnly"
            :min-date="minDateRef"
            :max-date="maxDateRef"
            :required="isRequired"
            :popover="false"
            :masks="masks"
            v-model="refDateInput"
            v-bind="bindObj"
            @popover-did-hide="handleWrapBlur"
          >
            <template #default="{ togglePopover, inputValue, inputEvents }">
              <div class="w-full relative">
                <input
                  autocomplete="on"
                  :class="[{ 'border-s-4 border-s-primary': isRequired }]"
                  :value="inputValue"
                  :disabled="withIsDisabled"
                  placeholder="dd/MM/yyyy"
                  type="text"
                  class="w-full ps-3 py-1.5 rounded-sm border border-brand-grayblue-300 outline-none focus:border-primary read-only:bg-brand-gray-250 placeholder:normal-case disabled:cursor-not-allowed"
                  v-on="inputEvents"
                  @blur="handleWrapBlur"
                />

                <div
                  class="border-x border-x-gray-300 absolute top-0 end-0 h-full flex items-center gap-x-4 px-3"
                  @click.prevent="handleCalendarIcon(togglePopover)"
                >
                  <button>
                    <FontAwesomeIcon :icon="faCalendarAlt" class="text-brand-black-secondary" />
                  </button>
                </div>
              </div>
            </template>
          </VDatePicker>
        </tippy>
        <InputAppendage
          v-if="Array.isArray(appendages?.bottom)"
          :font-weight="appendages.font_weight"
          v-bind="appendages"
        >
          <div v-for="(item, key) in appendages?.bottom" :key="key">
            {{ $t(item) }}
          </div>
        </InputAppendage>
      </div>
    </div>
    <div v-if="hasError" class="w-full flex flex-col md:flex-row gap-x-4 gap-y-1 md:items-center">
      <div v-if="showErrorSpacer" class="w-full md:w-1/3"></div>
      <span :class="[getErrorColor(errorMessage)]" class="w-full pt-[2px] ltr:font-sans text-xs">
        {{ errorMessage }}
      </span>
    </div>
  </div>
</template>

<script setup>
import HintTippy from '@/components/Form/HintTyppy.vue'
import useDatePickerAction from '@/composables/actions/useDatePickerAction'
import useFormatDate from '@/composables/useFormatDate'
import useRuleHandler from '@/composables/useRuleHandler'
import useVisible from '@/composables/useVisible'
import useWatchAction from '@/composables/watchActions/index'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons'
import { DateTime } from 'luxon'
import { useField } from 'vee-validate'
import { computed, onMounted, onUnmounted, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import InputAppendage from '@/components/Form/InputAppendage.vue'
import lodashGet from 'lodash/get'

const { formateDateFromDDMMYYYY } = useFormatDate()
const defaultDateFormat = 'dd/MM/yyyy'
const formStore = useFormStore()
const i18n = useI18n()
const route = useRoute()

const props = defineProps({
  // Take after info-label
  readOnly: {
    type: Boolean,
    default: false
  },
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  defaultValue: Date,
  placeholder: String,
  hint: String,
  isDisabled: Boolean,
  minDate: [String, Date, Object],
  maxDate: [String, Date, Object],
  dependentOn: String,
  rules: Object,
  serverDateFormat: {
    type: String,
    default: () => 'dd/MM/yyyy'
  },
  direction: {
    type: String,
    default: 'flex-row md:items-center flex-col md:flex-row'
  },
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  showErrorSpacer: {
    type: Boolean,
    default: () => true
  },
  trigger: String,
  onBlur: Object,
  onWatch: Object,
  web: Object,
  appendages: Object,
  popoverContent: [Object, String]
})

const { findShowRule, getBindObj } = useVisible()

const fieldName = toRef(props, 'fieldName')
const minDateRef = ref()
const maxDateRef = ref()
const minDateWatch = ref()
const maxDateWatch = ref()
// for v-datepicker DD should be uppercase. but on luxon DD means localized date
const masks = ref({
  input: 'DD/MM/YYYY'
})

if (props.maxDate == '__now__') {
  maxDateRef.value = DateTime.now().toFormat(defaultDateFormat)
}
if (props.minDate == '__now__') {
  minDateRef.value = DateTime.now().toFormat(defaultDateFormat)
}
if (props.minDate?.ref) {
  minDateRef.value =
    lodashGet(formStore.form.values, props.minDate.ref) ||
    lodashGet(formStore.extraMap, props.minDate.ref)
}
if (props.maxDate?.ref) {
  maxDateRef.value =
    lodashGet(formStore.form.values, props.maxDate.ref) ||
    lodashGet(formStore.extraMap, props.maxDate.ref)
}

// v-datepicker issue: pass "" can't pick any date.
const bindObj = computed(() => {
  const res = {}
  if (minDateRef.value) {
    res['minDate'] = formateDateFromDDMMYYYY(minDateRef.value).toJSDate()
  }
  if (maxDateRef.value) {
    res['maxDate'] = formateDateFromDDMMYYYY(maxDateRef.value).toJSDate()
  }
  return res
})

const hasError = computed(() => errorMessage.value && meta.touched)

const withIsDisabled = computed(() => {
  if (typeof props.isDisabled === 'boolean') {
    return props.isDisabled
  } else if (props.isDisabled && Object.keys(props.isDisabled).length > 0) {
    const result = findShowRule(
      props.isDisabled.any || props.isDisabled.all,
      props.isDisabled.any ? 'any' : 'all',
      getBindObj(route.params.serviceId)
    )

    return result
  } else {
    return false
  }
})

const {
  errorMessage,
  handleChange,
  handleBlur,
  meta,
  value: dateInputValue
} = useField(
  fieldName,
  (value) => {
    return useRuleHandler(value, props.rules)
  },
  {
    initialValue: props.defaultValue
  }
)

const handleWrapBlur = () => {
  handleBlur()
  if (props.onBlur && props.onBlur.action) {
    useDatePickerAction()[props.onBlur.action](dateInputValue.value, props.onBlur.action_params)
  }
}

const handleCalendarIcon = (togglePopover) => {
  if (withIsDisabled.value) return
  togglePopover()
}
onMounted(() => {
  /**
   * if there's a default value and there's a value in the draft.request object
   */
  if (props.defaultValue) {
    dateInputValue.value = DateTime.fromISO(props.defaultValue).toFormat(defaultDateFormat)
  }
})

onUnmounted(() => {
  if (minDateWatch.value) {
    minDateWatch.value()
  }
  if (maxDateWatch.value) {
    maxDateWatch.value()
  }
})

if (props.minDate?.ref) {
  minDateWatch.value = watch(
    () =>
      lodashGet(formStore.form.values, props.minDate.ref) ||
      lodashGet(formStore.extraMap, props.minDate.ref),
    async (newValue) => {
      if (newValue) {
        minDateRef.value = newValue
      }

      // only reset this field if touched
      if (meta.touched) {
        handleChange(null)
      }
    }
  )
}

if (props.maxDate?.ref) {
  maxDateWatch.value = watch(
    () =>
      lodashGet(formStore.form.values, props.maxDate.ref) ||
      lodashGet(formStore.extraMap, props.maxDate.ref),
    async (newValue) => {
      if (newValue) {
        maxDateRef.value = newValue
      }

      // only reset this field if touched
      if (meta.touched) {
        handleChange(null)
      }
    }
  )
}

const t9nLabel = computed(() => i18n.t(props.label))

// const isRequired = computed(() => !!props.rules?.find((r) => r.name == 'required' || 'required_ui'))
const isRequired = computed(() => {
  const requiredReactive = props.rules?.find((r) => r.name == 'required_reactive')
  if (requiredReactive) {
    return isRequiredByReactive(requiredReactive)
  }
  return !!props.rules?.find((r) => ['required', 'required_ui'].includes(r.name))
})

const refDateInput = computed({
  get() {
    if (dateInputValue.value) {
      if (dateInputValue.value instanceof Date) {
        return dateInputValue.value
      } else {
        return formateDateFromDDMMYYYY(dateInputValue.value).toJSDate()
      }
    }

    return null
  },
  set(newValue) {
    dateInputValue.value = DateTime.fromJSDate(newValue).toFormat('dd/MM/yyyy')
  }
})

if (props.trigger == 'watch') {
  useWatchAction()[props.onWatch.action]()
}

/**
 * provides support for the required rule based on certain values
 * reason:
 * due to sharing forms, it's inevitable to have clashing logic on
 * some of the schema. this is a way to support both while still
 * enabling sharing between services.
 * {
 *     "name": "required_reactive",
 *     "value": {
 *         "operator": "in",
 *         "value": [
 *             33
 *         ],
 *         "ref": "serviceTransactionId"
 *     },
 *     "message": "INVALID_REQUIRED"
 * }
 */
const isRequiredByReactive = (rule) => {
  const { operator, value, ref } = rule.value

  let compareAgainst
  if (ref == 'serviceTransactionId') {
    compareAgainst = useGlobalStore().startedService.serviceTransactionId
  } else {
    compareAgainst = formStore.form.values[ref]
  }

  switch (operator) {
    case 'in':
      return value.includes(compareAgainst)
    case 'not_in':
      return [undefined, ...value].includes(compareAgainst) === false
    case 'not_null':
      return !!compareAgainst
    default:
      return false
  }
}

/**
 * determines the color of the error message.
 * if you need to add a warning, please get the translations
 * as the rule handler returns the translated value already.
 */
const getErrorColor = (errorMessage) => {
  const warnings = [i18n.t('passportExpiryMessageWorning'), i18n.t('PassportExpiryOrOneMonthLeft')]
  return warnings.includes(errorMessage) ? 'text-yellow-600' : 'text-brand-red'
}
</script>

<style lang="scss" scoped></style>
